import type { RepoMap } from '../types/environment';
import type { Task } from '../types/task';

export type NormalizedPrStatus = 'open' | 'closed' | 'merged' | 'draft';

const TERMINAL_TURN_STATUSES = new Set(['done', 'failed']);

export function normalizePrStatus(status?: string): NormalizedPrStatus | undefined {
    if (!status) {
        return undefined;
    }
    const normalized = status.trim().toLowerCase();
    if (normalized === 'open' || normalized === 'closed' || normalized === 'merged' || normalized === 'draft') {
        return normalized;
    }
    return undefined;
}

export function summarizePrStatuses(repoInfo?: RepoMap): {
    open: number;
    closed: number;
    merged: number;
    draft: number;
    total: number;
} {
    let open = 0;
    let closed = 0;
    let merged = 0;
    let draft = 0;

    if (repoInfo) {
        Object.values(repoInfo).forEach((info) => {
            const normalized = normalizePrStatus(info?.PRStatus);
            if (normalized === 'open') {
                open += 1;
            } else if (normalized === 'closed') {
                closed += 1;
            } else if (normalized === 'merged') {
                merged += 1;
            } else if (normalized === 'draft') {
                draft += 1;
            }
        });
    }

    const total = open + closed + merged + draft;
    return { open, closed, merged, draft, total };
}

export interface TaskStatusResult {
    label: string;
    variant: 'running' | 'failed' | 'open' | 'partial-open' | 'closed' | 'merged' | 'draft';
}

export function computeTaskStatus(task: Task): TaskStatusResult | null {
    const lastStatus = (task.LastTurnStatus ?? task.Status ?? '').trim();
    const normalizedLastStatus = lastStatus.toLowerCase();

    if (normalizedLastStatus && !TERMINAL_TURN_STATUSES.has(normalizedLastStatus)) {
        return { label: 'Running', variant: 'running' };
    }

    if (normalizedLastStatus === 'failed') {
        return { label: 'Failed', variant: 'failed' };
    }

    const { open, closed, merged, draft, total } = summarizePrStatuses(task.RepoInfo);
    if (total === 0) {
        return null;
    }

    const formatLabel = (base: string, count: number) => {
        if (total <= 1) {
            return base;
        }
        return `${base} (${count}/${total})`;
    };

    if (closed > 0) {
        return { label: formatLabel('Closed', closed), variant: 'closed' };
    }
    if (draft > 0) {
        return { label: formatLabel('Draft', draft), variant: 'draft' };
    }
    if (open > 0) {
        const variant = open === total ? 'open' : 'partial-open';
        return { label: formatLabel('Open', open), variant };
    }
    if (merged > 0) {
        return { label: formatLabel('Merged', merged), variant: 'merged' };
    }
    return null;
}
